import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter, useParams } from 'src/routes/hook';
// assets
import { countries } from 'src/assets/data';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { enqueueSnackbar, useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField, RHFAutocomplete, RHFSelect } from 'src/components/hook-form';
import {
  endpoints,
  getFetcher1,
  getFetcherPrams,
  postFetcher,
  postFetcher1,
  postPramsFetcher1,
  putFetcher1,
} from 'src/utils/axios';
import 'react-phone-number-input/style.css';
import TourGuideItemSchemaListUI from './tour-guide-item-schema-list-ui';

// ----------------------------------------------------------------------
// Form validation schema
const TourGuideSchema = Yup.object().shape({
  headerId: Yup.object().nullable().required('Schema is required'),
  items: Yup.array()
    .min(1, 'At least one item is required')
    .of(
      Yup.object().shape({
        id: Yup.mixed().required('Item ID is required'),
      })
    )
    .required('Items are required'),
});
export default function TourGuideItemEditForm({ currentUser, isEditMode }) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [schemaName, setSchemaName] = useState('');
  // const [items, setItems] = useState("");
  const [itemList, setItemList] = useState([]);
  const [itemName, setItemName] = useState('');
  const [categoryLevel, setCategoryLevel] = useState(0);
  const [toolbarSchemaId, setToolbarSchemaId] = useState(0);
  const [search, setSearch] = useState('');

  console.log('tableData', tableData);

  // Default values for the form
  const defaultValues = useMemo(
    () => ({
      type: 1,
      discountMethod: 1,
      isEnabled,
      categoryLevel: 0,
      items: [],
      // isEnabled,
      // categoryLevel: currentUser?.categoryLevel ?? 0,
    }),
    [isEnabled]
  );

  // React Hook Form methods
  const methods = useForm({
    resolver: yupResolver(TourGuideSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Simulate loading state
  const loadingSend = useBoolean(false);
  // fetch api
  const fetchLines = useCallback(async () => {
    setLoading(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      setTableLoading(true);
      const param = {
        name: search === null ? '' : search || '',
        schemaId: toolbarSchemaId?.id || 0,
      };
      const response = await postPramsFetcher1(
        `${endpoints.tourGuide.createTourGuideLineItem}/${page}/${size}/${0}`,
        null,
        param,
        config
      );
      if (!response || (response.status !== 201 && response.status !== 200)) {
        setTableData(null);

        throw new Error(response?.message || 'Failed to fetch lines');
      }
      console.log('Response from fetchLineItems:', response);

      setTableData(response?.data);
    } catch (error) {
      setTableData(null);
      console.error('Error fetching schemas:', error);
    }
    setTableLoading(false);
    setLoading(false);
  }, [search, toolbarSchemaId, page, size]);

  // create api call
  const handleCreateAndSend = handleSubmit(async (data) => {
    console.log('handleCreateAndSend called with data:', data);
    loadingSend.onTrue();
    // @RequestBody List < TourGuideSchemaLineItem > schema

    const payload = methods.getValues('items').map((item) => ({
      itemId: item?.id,
      headerId: methods.getValues('headerId')?.id || null,
      isEnabled,
    }));

    console.log('Payload for API call:', payload);

    try {
      const response =
        updateId === null
          ? await postFetcher1(endpoints.tourGuide.createTourGuideLineItem, payload)
          : await putFetcher1(
              `${endpoints.tourGuide.createTourGuideLineItem}/${updateId}`,
              payload
            );
      await fetchLines();
      if (!response || (response.status !== 201 && response.status !== 200)) {
        enqueueSnackbar('Failed to create tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
        setIsEnabled(true);
        throw new Error(response?.message || 'Failed to create tour guide schema');
      } else {
        enqueueSnackbar('Tour guide schema created successfully', {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: {
            width: '400px',
          },
        });
        // Reset form if needed
        methods.reset({
          id: updateId || null,
          configurationName: '',
          headerId: null,
          type: 1,
          discountMethod: 1,
          amount: '',
          categoryLevel: 0,
          categoryId: null,
          isEnabled,
        });

        methods.reset();
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Show success message or handle response
    } catch (error) {
      enqueueSnackbar('Failed to create tour guide schema', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        sx: {
          width: '400px',
        },
      });
    } finally {
      loadingSend.onFalse();
    }
    setUpdateId(null);
  });

  const setToolbarSchemaIdFromChild = (id, name) => {
    console.log('setToolbarSchemaIdFromChild called with id:', id, ' name:', name);
    setToolbarSchemaId(id);
    setSearch(name);
  };

  const fetchTourGuideSchemas = useCallback(async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const param = {
        name: schemaName || '',
      };
      const response = await postPramsFetcher1(
        `${endpoints.tourGuide.createTourGuideLine}/${0}/${10}/${0}`,
        null,
        param,
        config
      );

      console.log('Response from fetchTourGuideSchemas:', response?.data?.content);

      if (response && response.status === 200) {
        setSchemaList(response?.data?.content || []);
      }
    } catch (error) {
      console.log('Error fetching tour guide schemas:', error);
    }
  }, [schemaName]);

  const handleEditRowEnable = useCallback(
    async (row, isEnabled2) => {
      setLoading(true);
      console.log('rowFire', row);
      let isEnabled1;
      if (isEnabled2 === true || isEnabled2 === 'true' || isEnabled2 === '1') {
        isEnabled1 = false;
      } else {
        isEnabled1 = true;
      }

      try {
        const payload = {
          ...row,
          isEnabled: isEnabled1,
        };
        const response = await putFetcher1(
          `${endpoints.tourGuide.createTourGuideLineItem}/${row.id}`,
          payload
        );
        if (!response || response.status !== 200) {
          enqueueSnackbar('Failed to update tour guide schema', {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });
        } else {
          enqueueSnackbar('Tour guide schema updated successfully', {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { width: '400px' },
          });

          fetchTourGuideSchemas();
        }
      } catch (error) {
        enqueueSnackbar('Failed to update tour guide schema', {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          sx: { width: '400px' },
        });
      }

      await fetchLines();
    },
    [fetchTourGuideSchemas, fetchLines]
  );

  useEffect(() => {
    function fetchTourGuideSchemas1() {
      fetchTourGuideSchemas();
    }
    fetchTourGuideSchemas1();
  }, [fetchTourGuideSchemas]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handler to set the page size
  const handleSizeChange = (newSize) => {
    setSize(newSize);
    // setPage(0); // Optionally reset to first page when size changes
  };

  const handleEditRow = useCallback(
    async (row) => {
      try {
        // Push row?.schemaList to schemaList if not already present (by id)
        if (row?.tourGuideSchema && !schemaList.some((s) => s.id === row.schemaList.id)) {
          setSchemaList((prev) => [...prev, row.tourGuideSchema]);
        }
      } catch (error) {
        console.log('Error adding category:', error);
      }

      console.log('Editing row:', row);
      methods.setValue('headerId', row?.headerId || null);
      setIsEnabled(row?.isEnabled);

      setUpdateId(row?.id || null);
      if (row?.type === 1) {
        setCategoryLevel(row?.categoryLevel || 0);
      }
    },
    [methods, schemaList]
  );

  useEffect(() => {
    // materialMaster
    async function fetchMaterialMaster() {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const param = {
        item_id: itemName || '',
      };

      const response = await postPramsFetcher1(
        endpoints.materialMaster.searchMaster,
        null,
        param,
        config
      );
      if (!response || (response.status !== 201 && response.status !== 200)) {
        throw new Error(response?.message || 'Failed to fetch material master');
      }
      if (response && response.status === 200) {
        setItemList(response.data || []);
      }
      console.log('responsee', response);

      // You can process response.data here if needed
    }
    fetchMaterialMaster();
  }, [itemName]);

  console.log('Fetching schemas with page:', page, 'size:', size);

  useEffect(() => {
    fetchLines();
  }, [page, size, toolbarSchemaId, search, fetchLines]);

  const setNameFromChild = (name) => {
    // setName(name);
  };
  return (
    <FormProvider methods={methods}>
      <Grid container spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Schema Item Details</Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
              sx={{ mb: 2 }}
            >
              {/* <RHFTextField name="configurationName" label="Configuration Name" size="small" value={methods.watch('configurationName') || ''} /> */}
              <RHFAutocomplete
                name="headerId"
                label="Schema Configuration Name"
                size="small"
                options={schemaList}
                value={methods.watch('headerId') || null}
                getOptionLabel={(option) => option.configurationName || ''}
                onInputChange={(_, value) => {
                  setSchemaName(value);
                }}
                onChange={(_, value) => {
                  methods.setValue('headerId', value || null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option?.configurationName || '-'}
                  </li>
                )}
              />

              <RHFAutocomplete
                name="items"
                label="Items"
                size="small"
                multiple
                freeSolo={false}
                options={itemList}
                value={methods.watch('items') || []}
                getOptionLabel={(option) => option.name || ''}
                onInputChange={(_, value) => {
                  setItemName(value);
                }}
                onChange={(_, value) => {
                  console.log('Input changed:', value);

                  methods.setValue('items', value || []);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <input type="checkbox" checked={selected} style={{ marginRight: 8 }} readOnly />
                    {option?.name || '-'}
                  </li>
                )}
              />
            </Box>
            <Stack justifyContent="flex-end" direction="row" spacing={2} sx={{ mt: 2 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loadingSend.value && isSubmitting}
                onClick={handleCreateAndSend}
                size="small"
              >
                {updateId === null ? 'Create Tour Guide Schema' : 'Update Tour Guide Schema'}
              </LoadingButton>
            </Stack>
          </Card>
          <br />
          <br />
          <TourGuideItemSchemaListUI
            tableLoading={tableLoading}
            handlePageChange={handlePageChange}
            handleSizeChange={handleSizeChange}
            tableDataFromParent={tableData}
            handleEditRowParent={handleEditRow}
            handleEditRowEnable={handleEditRowEnable}
            setNameFromChild={setNameFromChild}
            loading={loading}
            setToolbarSchemaIdFromChild={setToolbarSchemaIdFromChild}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
}

TourGuideItemEditForm.propTypes = {
  currentUser: PropTypes.object,
  isEditMode: PropTypes.bool,
};
